import React, { useEffect, useState } from 'react';
import { AutoConfirmedAlert, HtmlString, OverlaySpinner } from '@adjust/dashboard-components';
import { curriedUniq } from '@adjust/dashboard-core/lib/fp';

import { GoogleSsoConfig, SsoSettings } from '@api/types';
import { connectWithStore } from '@common/connectWithStore';
import { SidebarId } from '@react/constants/sidebars';
import { Trace, TraceStatus } from '@redux/types';
import {
  closeAlert,
  fetchData,
  createData,
  updateData,
  getGoogleSsoConfig,
  parseSettings,
  clearSettings,
} from '@redux/ducks/ssoConfiguration/googleSso';
import { SsoConfigurationForm, SsoConfigurationFormValues } from './SsoConfigurationForm';
import { SsoConfigurationPreview } from './SsoConfigurationPreview';

import './SsoConfiguration.scss';

interface SsoConfigurationProps {
  closeAlert: () => void;
  closeSidebar: () => void;
  config: Nullable<GoogleSsoConfig>;
  parsedSettings: Nullable<SsoSettings>;
  trace: Trace;
  isAlertOpen: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  isParsing: boolean;
  fetchConfiguration: () => void;
  createConfiguration: (params: GoogleSsoConfig) => void;
  updateConfiguration: (params: GoogleSsoConfig) => void;
  parseSettings: (param: File) => void;
  clearSettings: () => void;
}

const SsoConfiguration: React.FC<SsoConfigurationProps> = ({
  closeAlert,
  closeSidebar,
  config,
  isLoading,
  isSubmitting,
  isAlertOpen,
  trace,
  fetchConfiguration,
  createConfiguration,
  updateConfiguration,
  parsedSettings,
  parseSettings,
  isParsing,
  clearSettings,
}) => {
  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  // Close Alert when the component is unmounting
  useEffect(
    () => () => {
      if (isAlertOpen) {
        closeAlert();
      }
    },
    [isAlertOpen, closeAlert],
  );

  // Clear parsed settings when the component is unmounting
  useEffect(
    () => () => {
      if (parsedSettings) {
        clearSettings();
      }
    },
    [parsedSettings, clearSettings],
  );

  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, selectFile] = useState<File | null>(null);

  const { status, message } = trace;

  const onAlertConfirm = () => {
    if (trace.status === TraceStatus.Info) {
      closeSidebar();
    } else {
      if (!parsedSettings && selectedFile) {
        selectFile(null);
      }

      closeAlert();
    }
  };

  const onSubmit = (params: SsoConfigurationFormValues) => {
    const settings = parsedSettings ? parsedSettings : config?.settings;
    const newConfig = { domains: curriedUniq(params.domains), settings } as GoogleSsoConfig;

    if (isEditing) {
      updateConfiguration(newConfig);
    } else {
      createConfiguration(newConfig);
    }
  };

  const onFormCancel = () => {
    if (isEditing) {
      setIsEditing(false);
      clearSettings();
      selectFile(null);
    } else {
      closeSidebar();
    }
  };

  const onPreviewCancel = () => {
    closeSidebar();
  };

  const onEdit = () => {
    setIsEditing(true);
  };

  const fileSelectHandler = (files: File[]) => {
    selectFile(files[0]);
    parseSettings(files[0]);
  };

  const fileDeselectHandler = () => {
    clearSettings();
    selectFile(null);
  };

  const fileName = config && !parsedSettings && !selectedFile ? t('sso.idp-file.example') : selectedFile?.name;

  return (
    <div className="flex-box-column flex-one content-panel sso-configuration">
      <div className="content-title-box without-background flex-auto">
        <h3 className="content-heading">{t('sso.header.title', { provider: t('label.google') })}</h3>
        <p className="info small">
          <HtmlString>{t('sso.header.description')}</HtmlString>
        </p>
      </div>
      <div className="sidebar-view flex-one flex-box-column">
        {config && !isEditing ? (
          <SsoConfigurationPreview domains={config.domains} onEdit={onEdit} onCancel={onPreviewCancel} />
        ) : (
          <>
            <SsoConfigurationForm
              config={config}
              onSubmit={onSubmit}
              onCancel={onFormCancel}
              isLoading={isLoading}
              isSubmitting={isSubmitting}
              isEditing={isEditing}
              parsedSettings={parsedSettings}
              onFileSelect={fileSelectHandler}
              onFileDeselect={parsedSettings ? fileDeselectHandler : undefined}
              selectedFileName={fileName}
            />
            <OverlaySpinner isLoading={isParsing} />
          </>
        )}
      </div>
      <AutoConfirmedAlert
        isOpen={isAlertOpen}
        type={status}
        message={message}
        onConfirm={onAlertConfirm}
        parentSidebarId={SidebarId.SsoConfiguration}
      />
    </div>
  );
};

const mapStateToProps = state => {
  const { isAlertOpen, trace, isLoading, isSubmitting, config, parsedSettings, isParsing } = getGoogleSsoConfig(state);

  return {
    config,
    isLoading,
    isSubmitting,
    isAlertOpen,
    trace,
    parsedSettings,
    isParsing,
  };
};

const mapDispatchToProps = {
  fetchConfiguration: fetchData,
  createConfiguration: createData,
  updateConfiguration: updateData,
  parseSettings,
  clearSettings,
  closeAlert,
};

export default connectWithStore(mapStateToProps, mapDispatchToProps)(SsoConfiguration);

export { SsoConfiguration };
